import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import ListingCard from "../components/ListingCard";
import MapView from "../components/MapView";
import MarketplaceFilters from "../components/MarketplaceFilters";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nProvider";
import { Map as MapIcon, LayoutGrid, Globe, Users, Building2, ArrowRight, X as XIcon, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";
import MoveMaterialModal from "../components/MoveMaterialModal";

function MarketplaceResults({ loading, view, listings, center, t, selectable, selectedIds, onToggle }) {
  if (loading) {
    return <div className="p-12 label-tech">{t("market.loading")}</div>;
  }
  if (view === "map") {
    return <MapView listings={listings} center={center ? [center.lat, center.lng] : null} />;
  }
  if (listings.length === 0) {
    return (
      <div className="bg-[var(--bone)] p-16 text-center">
        <div className="label-tech">{t("market.emptyLabel")}</div>
        <h3 className="font-display text-2xl mt-3">{t("market.emptyTitle")}</h3>
        <p className="text-[var(--ink-700)] mt-2">{t("market.emptyBody")}</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--ink-200)]">
      {listings.map((l) => (
        <div key={l.id} className="relative">
          {selectable && (
            <button
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onToggle(l.id); }}
              className={`absolute top-3 left-3 z-10 w-9 h-9 grid place-items-center border ${selectedIds.has(l.id) ? "bg-[var(--brick)] text-[var(--bone)] border-[var(--brick)]" : "bg-[var(--bone)]/95 border-[var(--ink-300)] text-[var(--ink-500)] hover:text-[var(--ink)]"}`}
              data-testid={`listing-select-${l.id}`}
              aria-label="Select"
            >
              {selectedIds.has(l.id) ? <CheckSquare size={16} /> : <Square size={16} />}
            </button>
          )}
          <ListingCard listing={l} />
        </div>
      ))}
    </div>
  );
}

export default function Marketplace() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("grid");
  const [center, setCenter] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkMove, setShowBulkMove] = useState(false);

  const filters = useMemo(() => ({
    category: params.get("category") || "All",
    quality: params.get("quality") || "",
    location: params.get("location") || "",
    search: params.get("search") || "",
    pickup: params.get("pickup") === "true",
    delivery: params.get("delivery") === "true",
    radius: Number(params.get("radius") || 0),
    scope: params.get("scope") || "public",
    project_id: params.get("project_id") || "",
  }), [params]);

  const selectable = !!user?.organization_id && filters.scope === "own";
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // Projects for sub-filter chips (only relevant on shared/own tabs)
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    if (!user?.organization_id || filters.scope === "public") {
      setProjects([]);
      return;
    }
    const includeCollab = filters.scope === "shared";
    api.get("/projects", { params: { include_collab: includeCollab } })
      .then((r) => setProjects(Array.isArray(r.data) ? r.data : []))
      .catch(() => setProjects([]));
  }, [user, filters.scope]);

  // Group projects by org so the chips can render "[Partner Org] · Project name"
  const orgIdToName = useMemo(() => {
    const map = {};
    for (const p of projects) {
      if (p.organization_id && p.organization_name) map[p.organization_id] = p.organization_name;
    }
    return map;
  }, [projects]);

  useEffect(() => {
    const q = {};
    if (filters.category && filters.category !== "All") q.category = filters.category;
    if (filters.quality) q.quality = filters.quality;
    if (filters.location) q.location = filters.location;
    if (filters.search) q.search = filters.search;
    if (filters.pickup) q.pickup = true;
    if (filters.delivery) q.delivery = true;
    if (center && filters.radius > 0) {
      q.center_lat = center.lat;
      q.center_lng = center.lng;
      q.radius_km = filters.radius;
    }
    // Only authenticated users with an org can request shared/own scopes.
    if (filters.scope && filters.scope !== "all") q.visibility_scope = filters.scope;
    if (filters.project_id) q.project_id = filters.project_id;
    setLoading(true);
    api.get("/listings", { params: q })
      .then((r) => setListings(r.data))
      .finally(() => setLoading(false));
  }, [filters, center]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === "" || value === null || value === false || value === "All" || value === 0) next.delete(key);
    else next.set(key, value === true ? "true" : value);
    // Switching scope drops the project filter — it's scope-specific.
    if (key === "scope") { next.delete("project_id"); setSelectedIds(new Set()); }
    setParams(next);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error(t("market.geoUnsupported"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        if (filters.radius === 0) update("radius", 25);
        toast.success(t("market.centered"));
      },
      () => toast.error(t("market.geoFailed"))
    );
  };

  const clearCenter = () => {
    setCenter(null);
    update("radius", 0);
  };

  const reset = () => { setParams({ scope: filters.scope }); setCenter(null); };

  return (
    <Layout>
      <div className="max-w-[1440px] mx-auto px-6 pt-10">
        <div className="flex items-end justify-between">
          <div>
            <div className="label-tech">{t("market.label")}</div>
            <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tighter mt-2">{t("market.title")}</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="label-tech hidden md:inline">{listings.length} {t("market.items")}</span>
            <div className="flex border border-[var(--ink-200)]">
              <button
                onClick={() => setView("grid")}
                className={`px-3 py-2 text-xs font-mono-tech uppercase tracking-[0.1em] flex items-center gap-1 ${view === "grid" ? "bg-[var(--ink)] text-[var(--bone)]" : "bg-[var(--surface)]"}`}
                data-testid="view-grid-btn"
              >
                <LayoutGrid size={14} /> {t("market.grid")}
              </button>
              <button
                onClick={() => setView("map")}
                className={`px-3 py-2 text-xs font-mono-tech uppercase tracking-[0.1em] flex items-center gap-1 border-l border-[var(--ink-200)] ${view === "map" ? "bg-[var(--ink)] text-[var(--bone)]" : "bg-[var(--surface)]"}`}
                data-testid="view-map-btn"
              >
                <MapIcon size={14} /> {t("market.map")}
              </button>
            </div>
          </div>
        </div>

        {/* Visibility scope segment — only meaningful for authenticated org users */}
        <div className="mt-6 flex flex-wrap gap-px bg-[var(--ink-200)] border border-[var(--ink-200)] w-fit" role="tablist" data-testid="market-scope">
          {[
            { key: "public", icon: <Globe size={14} />, label: t("market.scopePublic"), hint: t("market.scopePublicHint") },
            ...(user?.organization_id ? [
              { key: "shared", icon: <Users size={14} />, label: t("market.scopeShared"), hint: t("market.scopeSharedHint") },
              { key: "own", icon: <Building2 size={14} />, label: t("market.scopeOwn"), hint: t("market.scopeOwnHint") },
            ] : []),
          ].map((s) => (
            <button
              key={s.key}
              onClick={() => update("scope", s.key)}
              className={`px-4 py-2.5 text-xs font-mono-tech uppercase tracking-[0.1em] flex items-center gap-2 ${filters.scope === s.key ? "bg-[var(--ink)] text-[var(--bone)]" : "bg-[var(--bone)] hover:bg-[var(--surface-alt)]"}`}
              title={s.hint}
              data-testid={`market-scope-${s.key}`}
              role="tab"
              aria-selected={filters.scope === s.key}
            >
              {s.icon} {s.label}
            </button>
          ))}
        </div>

        {/* Project sub-filter — chips shown for own/shared scopes only */}
        {filters.scope !== "public" && projects.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="market-projects">
            <span className="label-tech text-[var(--ink-500)] mr-1">{t("market.projectFilter")}</span>
            <button
              onClick={() => update("project_id", "")}
              className={`px-3 py-1.5 text-xs font-mono-tech uppercase tracking-[0.1em] border ${filters.project_id === "" ? "bg-[var(--ink)] text-[var(--bone)] border-[var(--ink)]" : "bg-[var(--bone)] border-[var(--ink-200)] hover:bg-[var(--surface-alt)]"}`}
              data-testid="market-project-all"
            >
              {t("market.projectAll")}
            </button>
            {projects.map((p) => (
              <button
                key={p.id}
                onClick={() => update("project_id", p.id)}
                className={`px-3 py-1.5 text-xs font-mono-tech uppercase tracking-[0.1em] border ${filters.project_id === p.id ? "bg-[var(--brick)] text-[var(--bone)] border-[var(--brick)]" : "bg-[var(--bone)] border-[var(--ink-200)] hover:bg-[var(--surface-alt)]"}`}
                title={p.namn}
                data-testid={`market-project-${p.id}`}
              >
                {filters.scope === "shared" && orgIdToName[p.organization_id] ? (
                  <>
                    <span className="opacity-60">{orgIdToName[p.organization_id]} · </span>
                    {p.namn}
                  </>
                ) : (
                  p.namn
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="max-w-[1440px] mx-auto px-6 mt-8 grid grid-cols-12 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
        <MarketplaceFilters
          filters={filters}
          center={center}
          onChange={update}
          onReset={reset}
          onUseMyLocation={useMyLocation}
          onClearLocation={clearCenter}
        />

        <section className="col-span-12 md:col-span-9 bg-[var(--surface-alt)] p-px">
          <MarketplaceResults
            loading={loading}
            view={view}
            listings={listings}
            center={center}
            t={t}
            selectable={selectable}
            selectedIds={selectedIds}
            onToggle={toggleSelect}
          />
        </section>
      </div>

      {/* Sticky bulk-action bar */}
      {selectable && selectedIds.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-[var(--ink)] text-[var(--bone)] border border-[var(--ink-200)] shadow-2xl px-5 py-3 flex items-center gap-4" data-testid="bulk-action-bar">
          <span className="font-mono-tech text-xs uppercase tracking-[0.1em]">
            {t("market.bulkSelected").replace("{n}", String(selectedIds.size))}
          </span>
          <button
            onClick={() => setShowBulkMove(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--brick)] text-[var(--bone)] font-mono-tech uppercase tracking-[0.1em] text-xs hover:opacity-90 transition-opacity"
            data-testid="bulk-move-btn"
          >
            <ArrowRight size={14} /> {t("market.bulkMove")}
          </button>
          <button
            onClick={clearSelection}
            className="text-[var(--bone)]/70 hover:text-[var(--bone)]"
            aria-label="Clear selection"
            data-testid="bulk-clear-btn"
          >
            <XIcon size={16} />
          </button>
        </div>
      )}

      {showBulkMove && (
        <MoveMaterialModal
          listingIds={Array.from(selectedIds)}
          onClose={() => setShowBulkMove(false)}
          onMoved={() => {
            clearSelection();
            // Re-fetch listings
            const q = {};
            if (filters.category && filters.category !== "All") q.category = filters.category;
            if (filters.scope && filters.scope !== "all") q.visibility_scope = filters.scope;
            if (filters.project_id) q.project_id = filters.project_id;
            api.get("/listings", { params: q }).then((r) => setListings(r.data));
          }}
        />
      )}
    </Layout>
  );
}
